import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import moment from "moment";
import { MouseEvent, memo, useMemo } from "react";

import AppIcon from "@/components/Core/AppIcon";
import { useTranslate } from "@/hooks";
import { colors } from "@/theme";
import { NotificationIcons } from "@/types";

import { NotificationListProps } from "./type";

/**
 * One notification, in the header's menu and on the notifications page.
 *
 * Read and unread used to differ only in the color of a small icon, and every
 * line of every row was the same grey. An unread one is now lit: a tinted row,
 * its icon on a filled tile, its title at full strength and a dot beside it.
 * A read one steps back to plain text. The two actions say what they do.
 */
const NotificationList = ({
  notification,
  full,
  stackProps,
  onMarkRead,
  onDelete,
}: NotificationListProps) => {
  const t = useTranslate();
  const timeAgo = useMemo(() => moment(notification.createdAt).fromNow(), [notification.createdAt]);
  const unread = !notification.isRead;

  const handleMarkRead = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onMarkRead?.(notification._id);
  };

  const handleDelete = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    onDelete?.(notification._id);
  };

  const { sx: stackSx, ...restStackProps } = stackProps ?? {};

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="flex-start"
      {...restStackProps}
      sx={[
        (theme) => ({
          position: "relative",
          p: 1.5,
          borderRadius: full ? "12px" : 0,
          border: full ? "1px solid" : 0,
          borderBottom: "1px solid",
          borderColor: full && unread ? alpha(colors.prime, 0.3) : theme.palette.divider,
          backgroundColor: unread ? alpha(colors.prime, 0.07) : "transparent",
          transition: "background-color 160ms ease",
          "@media (hover: hover)": {
            "&:hover": { backgroundColor: alpha(colors.prime, unread ? 0.12 : 0.05) },
          },
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        }),
        ...(Array.isArray(stackSx) ? stackSx : [stackSx]),
      ]}
    >
      <Box
        aria-hidden
        sx={(theme) => ({
          flexShrink: 0,
          width: 36,
          height: 36,
          display: "grid",
          placeItems: "center",
          borderRadius: "10px",
          color: unread ? colors.white : "#0B63D6",
          backgroundColor: unread ? colors.prime : alpha(colors.prime, 0.1),
          boxShadow: unread ? `0 4px 10px ${alpha(colors.prime, 0.35)}` : "none",
          "& svg": { width: 18, height: 18 },
          "& .app-icon": { color: "inherit" },
          ...theme.applyStyles("dark", {
            color: unread ? colors.white : "#5BC8FF",
            backgroundColor: unread ? colors.prime : alpha("#5BC8FF", 0.1),
          }),
        })}
      >
        <AppIcon name={NotificationIcons[notification.type]} size={18} />
      </Box>

      <Stack sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
        <Stack direction="row" alignItems="center" gap={0.75}>
          {notification.title && (
            <Typography
              variant="body2"
              color={unread ? "text.primary" : "text.secondary"}
              fontWeight={unread ? 700 : 600}
              noWrap
              sx={{ minWidth: 0 }}
            >
              {notification.title}
            </Typography>
          )}
          {unread && (
            <Box
              aria-label={t("notification.unread")}
              role="img"
              sx={{
                flexShrink: 0,
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: colors.prime,
              }}
            />
          )}
        </Stack>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            ...(full
              ? { whiteSpace: "pre-wrap", wordBreak: "break-word" }
              : {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }),
          }}
        >
          {notification.message}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.25 }}>
          {timeAgo}
        </Typography>
      </Stack>

      {(onMarkRead || onDelete) && (
        <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
          {onMarkRead && unread && (
            <Tooltip title={t("notification.mark")} arrow>
              <IconButton
                size="small"
                aria-label={t("notification.mark")}
                onClick={handleMarkRead}
                sx={{ "&:hover": { color: colors.prime } }}
              >
                <AppIcon name="check" size={16} />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title={t("notification.delete")} arrow>
              <IconButton
                size="small"
                aria-label={t("notification.delete")}
                onClick={handleDelete}
                sx={{ "&:hover": { color: "error.main" } }}
              >
                <AppIcon name="trash" size={16} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default memo(NotificationList);
